import React from 'react';
import './RatingBlock.css';
import { RatingStars } from '../RatingStars';
import { useTranslation } from 'react-i18next';

export interface RatingBlockProps {
  rating5: number; // percent 0..100
  rating4: number; // percent 0..100
  rating3: number; // percent 0..100
  rating2: number; // percent 0..100
  rating1: number; // percent 0..100
  reviewsCount: number;
  className?: string;
  style?: React.CSSProperties;
}

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

const Row: React.FC<{ label: number; percent: number }> = ({ label, percent }) => {
  const { t } = useTranslation();
  const p = clamp(percent, 0, 100);
  return (
    <div className="rb-row">
      <div className="rb-row-label" aria-hidden>
        <span className="rb-row-star">★</span>
        <span className="stars-count-number">{label}</span>
      </div>
      <div className="rb-row-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={p} aria-label={t('rating.percentAria', { label })}>
        <div className="rb-row-bar-fill" style={{ width: `${p}%` }} />
      </div>
      <div className="rb-row-percent">{p}%</div>
    </div>
  );
};

export const RatingBlock: React.FC<RatingBlockProps> = ({
  rating5,
  rating4,
  rating3,
  rating2,
  rating1,
  reviewsCount,
  className,
  style,
}) => {
  const commonRating = (rating5 * 5 + rating4 * 4 + rating3 * 3 + rating2 * 2 + rating1) / 100;
  const rating = clamp(commonRating, 0, 5);
  const classes = ['rating-block', className].filter(Boolean).join(' ');

  const { t } = useTranslation();
  return (
    <div className={classes} style={style}>
      <div className="rb-left">
        <div className="rb-left-rating">
          <div className="rb-score" aria-label={t('rating.averageAria', { rating: rating.toFixed(1) })}>
            {rating.toFixed(1)}
          </div>
          <RatingStars rating={rating} className="rb-stars" />
        </div>
        <div className="rb-reviews" aria-label={t('rating.reviews', { count: reviewsCount })}>
          {t('rating.reviews', { count: reviewsCount })}
        </div>
      </div>

      <div className="rb-right">
        <Row label={5} percent={rating5} />
        <Row label={4} percent={rating4} />
        <Row label={3} percent={rating3} />
        <Row label={2} percent={rating2} />
        <Row label={1} percent={rating1} />
      </div>
    </div>
  );
};

export default RatingBlock;
